import React, { useState } from 'react';
import styled from '@emotion/styled';
import Icon from '~ComponentsImproved/Icon';
import Shape from '~Assets/icons/category-button-shape.svg';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useInventoryHelpers } from '~Components/InventoryUniverse';
import { useI18n } from '~Hooks';
import { Badge } from '~Styles';

const CategoryButton = styled.div<{ selected: boolean; backgroundColor: string }>`
  width: 136px;
  height: 64px;
  min-height: 64px;
  display: flex;
  flex-flow: row nowrap;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  padding: 4px;
  box-sizing: border-box;
  position: relative;
  border-bottom: 1px solid var(--colorBlueGray1200);
  z-index: ${({ selected }) => (selected ? 1 : 0)};
  background-color: ${({ backgroundColor }) => backgroundColor};
  word-break: break-word;
  box-shadow: ${({ selected }) =>
    selected
      ? '0px 0px 8px rgb(0 0 0 / 16%), 0px 16px 24px rgb(0 0 0 / 16%), 0px 0px 32px rgb(0 0 0 / 12%)'
      : 'none'};

  & > .category-pointer {
    height: 100%;
    width: 8px;
    position: absolute;
    right: -7px;
    display: ${({ selected }) => (selected ? 'block' : 'none')};
  }
  & > h5 {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const OutOfStockBadge = styled(Badge)`
  padding: 2px 4px;
  border-radius: 12px;
  font-weight: 600;
  // Positioning
  position: absolute;
  font-size: 60%;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
`;

export interface Props {
  id: string;
  name: string;
  backgroundColor: string;
  isScrolling: boolean;
  active: boolean;
  handleCategoryClick: (id: string, name: string) => void;
}

const Category = ({
  id,
  name,
  backgroundColor = 'var(--colorWhite)', // Default background color
  isScrolling,
  active,
  handleCategoryClick,
}: Props) => {
  const { i18n } = useI18n();
  const { itemIsOutOfStock } = useInventoryHelpers();
  const [touching, setTouching] = useState(false);

  const categoryOutOfStock = itemIsOutOfStock(id);

  const onTouchStart = () => {
    setTouching(true);
  };

  const onTouchEnd = () => {
    if (!isScrolling && touching) {
      handleCategoryClick(id, name);
    }
    setTouching(false);
  };

  return (
    <CategoryButton
      key={id}
      selected={active}
      backgroundColor={backgroundColor}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}>
      {categoryOutOfStock && (
        <OutOfStockBadge backgroundColor="colorBlueGray1200" color="colorWhite">
          {i18n('Inventory.OutOfStock')}
        </OutOfStockBadge>
      )}
      <Heading level={5} color="var(--colorBlueGray1200)">
        {name}
      </Heading>
      <Icon src={Shape} color={backgroundColor} className="category-pointer" />
    </CategoryButton>
  );
};

export default React.memo(Category);
